import { useEffect, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "react-toastify";
import {
  assignLockboxToDeal,
  unassignLockboxFromDeal,
} from "../../utils/lockboxAssign";

// The "Assigned To" cell content while a lockbox row is being edited. Lets you
// pick a listing (searchable) and writes BOTH sides via assignLockboxToDeal, so
// the inventory and the listing stay in sync. Shared by the Electronic and
// Push Button tables. `deals` is the list of listings to choose from;
// `onChanged` reloads the table after a write.
export default function LockboxAssignCell({
  lb,
  lockboxType,
  deals,
  onChanged,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const isAssigned = lb.status === "Assigned" && lb.assignedDealId;

  const list = (deals || []).filter((d) => {
    const addr = (d.propertyaddress || d.propertyName || d.address || "")
      .toString()
      .toLowerCase();
    return addr.includes(query.trim().toLowerCase());
  });

  const assign = async (deal) => {
    setBusy(true);
    try {
      const ok = await assignLockboxToDeal({ lockbox: lb, lockboxType, deal });
      if (!ok) {
        toast.error("Could not assign — that listing is missing its id.");
        return;
      }
      toast.success(`Assigned to ${deal.propertyaddress || "listing"}`);
      setOpen(false);
      setQuery("");
      onChanged && (await onChanged());
    } catch (err) {
      console.error("Assign lockbox failed:", err);
      toast.error("Could not assign — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const unassign = async () => {
    setBusy(true);
    try {
      await unassignLockboxFromDeal({ lockbox: lb, lockboxType });
      toast.success("Lockbox unassigned");
      onChanged && (await onChanged());
    } catch (err) {
      console.error("Unassign lockbox failed:", err);
      toast.error("Could not unassign — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={ref} className="relative flex flex-col items-center gap-1">
      {isAssigned && (
        <span className="text-linkColor font-medium text-xs text-center line-clamp-2">
          {lb.assignedDealLabel || "Assigned"}
        </span>
      )}

      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={busy || disabled}
          title={disabled ? "Save the lockbox first, then assign a listing" : ""}
          onClick={() => setOpen((v) => !v)}
          className="text-xs font-semibold px-2 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
        >
          {busy ? (
            <Loader2 size={12} className="animate-spin" />
          ) : isAssigned ? (
            "Change"
          ) : (
            "Assign Listing"
          )}
        </button>
        {isAssigned && !busy && (
          <button
            type="button"
            onClick={unassign}
            title="Unassign"
            className="text-slate-400 hover:text-red-500 p-1 rounded-md"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute top-full mt-1 z-50 w-[260px] bg-white border border-gray-300 rounded-md shadow-lg p-2">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search listings…"
            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm mb-1 outline-none focus:border-themeColor"
          />
          <div className="max-h-[180px] overflow-y-auto">
            {list.length === 0 ? (
              <p className="text-xs text-gray-400 py-2 px-1">No listings match.</p>
            ) : (
              list.slice(0, 50).map((d) => (
                <button
                  key={d.createdat}
                  type="button"
                  onClick={() => assign(d)}
                  className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-50 line-clamp-1"
                >
                  {d.propertyaddress || d.propertyName || d.address || "Untitled listing"}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
